import ApplicationController from "./application_controller"
import Logger from '../lib/logger'

/**
 * MapAreaFilterController - Base controller for filtering by current map area.
 *
 * Finds the active Leaflet map, captures its visible bounds, injects them as a
 * hidden `map_bounds` parameter into the target form, and submits it.
 *
 * Usage:
 *   <button data-controller="map-area-filter"
 *           data-action="click->map-area-filter#filterByCurrentMapArea"
 *           data-map-area-filter-form-target-value="crm_search">
 *     Filter by Map Area
 *   </button>
 */
export default class extends ApplicationController {
  static values = {
    formTarget: String
  }

  // CSS classes for notifications (matches _map_viewer.scss)
  static NOTIFICATION_CLASSES = {
    base: 'map-ecosystem-notification',
    success: 'map-ecosystem-notification--success',
    error: 'map-ecosystem-notification--error'
  }

  connect() {
    super.connect()
    Logger.debug('Map area filter controller connected')
  }

  /**
   * Get the map-leaflet controller instance using Stimulus application.
   */
  getMapController() {
    try {
      const mapElement = document.querySelector('[data-controller*="map-leaflet"]')
      if (!mapElement) return null

      return this.application.getControllerForElementAndIdentifier(mapElement, 'map-leaflet')
    } catch (error) {
      Logger.warn('Failed to get map controller:', error)
      return null
    }
  }

  /**
   * Retrieve bounds string from the map controller.
   * Prefers public API getBoundsString(), falls back to raw Leaflet call.
   */
  getBoundsString(mapController) {
    // Try public API first
    if (typeof mapController.getBoundsString === 'function') {
      return mapController.getBoundsString()
    }

    // Fallback to raw Leaflet access
    if (mapController.map) {
      const bounds = mapController.map.getBounds()
      if (!bounds) return null
      const sw = bounds.getSouthWest()
      const ne = bounds.getNorthEast()
      return `${sw.lat},${sw.lng},${ne.lat},${ne.lng}`
    }

    return null
  }

  /**
   * Retrieve raw Leaflet bounds object (for internal calculations).
   */
  getRawBounds(mapController) {
    if (typeof mapController.getBounds === 'function') {
      return mapController.getBounds()
    }
    if (mapController.map) {
      return mapController.map.getBounds()
    }
    return null
  }

  /**
   * Main action: inject current map bounds into the target form and submit.
   */
  filterByCurrentMapArea(event) {
    event.preventDefault()
    event.stopPropagation()

    try {
      const mapController = this.getMapController()

      if (!mapController) {
        this.showError("Map not found. Please ensure the map is loaded.")
        return
      }

      // Prefer public API readiness check
      const isReady = typeof mapController.isReady === 'function'
        ? mapController.isReady()
        : !!mapController.map

      if (!isReady) {
        this.showError("Map controller not available. Please ensure the map is fully loaded.")
        return
      }

      const boundsString = this.getBoundsString(mapController)
      if (!boundsString) {
        this.showError("Could not get map bounds. Please try again.")
        return
      }

      // Validate bounds format
      const [swLat, swLng, neLat, neLng] = boundsString.split(',').map(Number)
      if (swLat >= neLat || swLng >= neLng) {
        this.showError("Invalid map area. Please adjust the map view and try again.")
        return
      }

      Logger.debug('Filtering by map area:', boundsString)

      // Show loading state
      const button = event.currentTarget
      button.disabled = true
      button.innerHTML = '<i class="fas fa-spinner fa-spin me-1"></i><span class="d-none d-sm-inline">Applying Filter...</span>'

      this.showSuccess("Applying filter by current map area...")

      // Store filter info in session storage for persistence
      const rawBounds = this.getRawBounds(mapController)
      if (rawBounds) {
        this.storeFilterInfo(boundsString, rawBounds)
      }

      const formTarget = this.formTargetValue

      if (!formTarget) {
        throw new Error("No form target specified. Set data-map-area-filter-form-target-value.")
      }

      const form = document.getElementById(formTarget)

      if (form) {
        this.injectBoundsField(form, boundsString)
        form.requestSubmit()
      } else {
        // Fallback: navigate via Turbo if available, otherwise window.location
        this.navigateToBoundsUrl(boundsString)
      }

    } catch (error) {
      Logger.error('Failed to filter by map area:', error)
      this.showError("Failed to apply map area filter. Please try again.")

      const button = event.currentTarget
      button.disabled = false
      button.innerHTML = '<i class="fas fa-map-marked-alt me-1"></i><span class="d-none d-sm-inline">Use Current Map Area</span>'
    }
  }

  /**
   * Inject or replace the map_bounds hidden field in a form.
   */
  injectBoundsField(form, boundsString) {
    const existingField = form.querySelector('input[name="map_bounds"]')
    if (existingField) {
      existingField.remove()
    }

    const hiddenField = document.createElement('input')
    hiddenField.type = 'hidden'
    hiddenField.name = 'map_bounds'
    hiddenField.value = boundsString
    form.appendChild(hiddenField)
  }

  /**
   * Fallback navigation when target form is not found.
   */
  navigateToBoundsUrl(boundsString) {
    const currentUrl = new URL(window.location.href)
    const searchParams = new URLSearchParams(currentUrl.search)
    searchParams.set('map_bounds', boundsString)
    const newUrl = `${currentUrl.pathname}?${searchParams.toString()}`

    if (typeof Turbo !== 'undefined') {
      Turbo.visit(newUrl, { action: "replace" })
    } else {
      window.location.href = newUrl
    }
  }

  /**
   * Persist filter metadata in sessionStorage.
   */
  storeFilterInfo(boundsString, bounds) {
    try {
      const filterInfo = {
        bounds: boundsString,
        timestamp: new Date().toISOString(),
        area: this.calculateApproximateArea(bounds)
      }
      sessionStorage.setItem('mapAreaFilter', JSON.stringify(filterInfo))
    } catch (error) {
      Logger.warn('Failed to store filter info:', error)
    }
  }

  /**
   * Rough km² approximation from Leaflet LatLngBounds.
   */
  calculateApproximateArea(bounds) {
    try {
      const sw = bounds.getSouthWest()
      const ne = bounds.getNorthEast()
      const latDiff = Math.abs(ne.lat - sw.lat)
      const lngDiff = Math.abs(ne.lng - sw.lng)
      return (latDiff * lngDiff * 12100).toFixed(1)
    } catch (error) {
      Logger.warn('Failed to calculate area:', error)
      return '0'
    }
  }

  /**
   * Create a notification alert element using CSS classes from _map_viewer.scss.
   */
  createNotification(message, type) {
    const alertDiv = document.createElement('div')
    const baseClass = this.constructor.NOTIFICATION_CLASSES.base
    const typeClass = type === 'success'
      ? this.constructor.NOTIFICATION_CLASSES.success
      : this.constructor.NOTIFICATION_CLASSES.error
    const alertClass = type === 'success' ? 'alert-success' : 'alert-danger'
    const iconClass = type === 'success' ? 'fa-check-circle' : 'fa-exclamation-triangle'

    alertDiv.className = `alert ${alertClass} alert-dismissible fade show ${baseClass} ${typeClass}`
    alertDiv.innerHTML = `
      <i class="fas ${iconClass} me-2"></i>${this.escapeHtml(message)}
      <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
    `

    return alertDiv
  }

  showSuccess(message) {
    const alertDiv = this.createNotification(message, 'success')
    document.body.appendChild(alertDiv)

    setTimeout(() => {
      if (alertDiv.parentNode) {
        alertDiv.remove()
      }
    }, 3000)
  }

  showError(message) {
    const alertDiv = this.createNotification(message, 'error')
    document.body.appendChild(alertDiv)

    setTimeout(() => {
      if (alertDiv.parentNode) {
        alertDiv.remove()
      }
    }, 5000)
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
